import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { motion } from 'framer-motion';
import { WalletIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

const Web3Demo = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [walletInfo, setWalletInfo] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState('');

  // Check if wallet is already connected on mount
  useEffect(() => {
    const checkExistingConnection = async () => {
      if (typeof window.ethereum !== 'undefined' && window.ethereum.selectedAddress) {
        try {
          const provider = new ethers.BrowserProvider(window.ethereum);
          const signer = await provider.getSigner();
          const address = await signer.getAddress();
          const balance = await provider.getBalance(address);
          const network = await provider.getNetwork();
          
          setWalletInfo({
            address,
            balance: ethers.formatEther(balance),
            network: network.name === 'unknown' ? `Chain ID: ${network.chainId}` : network.name
          });
          setIsConnected(true);
        } catch (err) {
          console.error('Error checking existing connection:', err);
        }
      }
    };

    checkExistingConnection();
  }, []);

  // Simulate wallet connection using the same approach as your working project
  const simulateWalletConnection = async () => {
    if (typeof window.ethereum === 'undefined') {
      setError('No wallet detected. Please install MetaMask or another wallet extension.');
      return;
    }

    setIsConnecting(true);
    setError('');

    try {
      // Use the same approach as your working project
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const address = await signer.getAddress();
      
      // Get balance and network info
      const balance = await provider.getBalance(address);
      const network = await provider.getNetwork();
      
      setWalletInfo({
        address,
        balance: ethers.formatEther(balance),
        network: network.name === 'unknown' ? `Chain ID: ${network.chainId}` : network.name
      });
      setIsConnected(true);
      
    } catch (err) {
      console.error('Wallet connection error:', err);
      setError(err.message || 'Failed to connect wallet');
    } finally {
      setIsConnecting(false);
    }
  };

  // Disconnect wallet
  const disconnectWallet = () => {
    setWalletInfo(null);
    setIsConnected(false);
    setError('');
  };

  // Format wallet address for display
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Web3 Wallet Connection Demo</h1>
      
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Test the Connect Wallet Functionality</h2>
        <p className="text-gray-600 mb-6">
          This is a demo page to verify that the Web3 wallet connection functionality is working properly.
          Try clicking the connect wallet button below:
        </p>
        
        <div className="flex justify-center mb-8">
          {!isConnected ? (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={simulateWalletConnection}
              disabled={isConnecting}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-8 py-4 rounded-xl font-semibold text-lg inline-flex items-center gap-3 transition-colors"
            >
              <WalletIcon className="w-6 h-6" />
              {isConnecting ? 'Connecting...' : 'Connect Wallet'}
            </motion.button>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-green-50 border border-green-200 rounded-2xl p-6 max-w-md mx-auto"
            >
              <div className="flex items-center justify-center gap-3 mb-4">
                <CheckCircleIcon className="w-8 h-8 text-green-600" />
                <span className="text-xl font-semibold text-green-800">Wallet Connected!</span>
              </div>
              <div className="space-y-2 text-sm text-green-700">
                <p><strong>Address:</strong> {formatAddress(walletInfo?.address)}</p>
                <p><strong>Balance:</strong> {walletInfo?.balance ? parseFloat(walletInfo.balance).toFixed(4) : '0'} ETH</p>
                <p><strong>Network:</strong> {walletInfo?.network}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={disconnectWallet}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  Disconnect
                </button>
              </div>
            </motion.div>
          )}
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4 max-w-md mx-auto"
          >
            <div className="flex items-center gap-3">
              <XCircleIcon className="w-6 h-6 text-red-600" />
              <span className="text-red-800 font-medium">{error}</span>
            </div>
            <div className="mt-3">
              <a
                href="https://metamask.io/download/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Install MetaMask →
              </a>
            </div>
          </motion.div>
        )}
        
        <div className="mt-8 text-center">
          <a href="/web3-connect" className="text-blue-600 hover:text-blue-700 font-medium underline">
            View Full Web3 Implementation →
          </a>
        </div>
        
        <div className="mt-8 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold mb-2">What to test:</h3>
          <ul className="list-disc list-inside text-gray-600 space-y-1">
            <li>Click "Connect Wallet" button</li>
            <li>MetaMask should prompt for connection</li>
            <li>After connection, button should show wallet address</li>
            <li>Click on connected button to see wallet details</li>
            <li>Test disconnect functionality</li>
          </ul>
        </div>
        
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold mb-2">Troubleshooting:</h3>
          <ul className="list-disc list-inside text-gray-600 space-y-1">
            <li>Make sure MetaMask is installed in your browser</li>
            <li>Check browser console for any JavaScript errors</li>
            <li>Ensure you're on a supported network (Ethereum mainnet, testnets)</li>
            <li>Try refreshing the page if connection fails</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Web3Demo;
